import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { EMAIL } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const EVENT_TYPES = ["Bridal", "Engagement", "Reception", "Haldi / Mehndi", "Party / Occasion", "Family & Friends", "Editorial / Photoshoot", "Other"];

const EMPTY = {
  name: "", email: "", phone: "", event_type: "", event_date: "",
  location: "", guest_count: "", style_preference: "", message: "", website: "",
};

const fieldCls =
  "w-full bg-transparent border-b border-[#E9DCC8] focus:border-[#B8893B] outline-none py-3 text-[15px] text-[#2A1F16] placeholder:text-[#9C8871] transition-colors duration-300";

const Label = ({ htmlFor, children, required }) => (
  <label htmlFor={htmlFor} className="block text-[11px] uppercase tracking-[0.22em] text-[#96754D] mb-1">
    {children} {required && <span className="text-[#B8893B]">*</span>}
  </label>
);

export const Inquiry = () => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      const res = await fetch(`${API}/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          Array.isArray(data.detail)
            ? "Please check the required fields and try again."
            : data.detail || "Something went wrong. Please try again."
        );
      }
      setStatus("success");
    } catch (err) {
      setError(err.message);
      setStatus("error");
    }
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <section id="inquire" data-testid="section-inquiry-form" className="py-24 sm:py-32 bg-[#F4EBDD]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1fr_1.2fr] gap-14 lg:gap-20">
        <Reveal>
          <Eyebrow index="10">Booking</Eyebrow>
          <h2 className="font-serif-ed font-normal text-4xl sm:text-5xl leading-[1.05] mt-6 text-[#2A1F16]">
            LET'S CREATE <em className="text-[#96754D]">YOUR LOOK</em>
          </h2>
          <p className="mt-6 text-sm sm:text-base leading-relaxed text-[#6B5A49] max-w-md">
            Planning your wedding, celebration or next shoot? Tell me a little about your event and
            I'll get back to you with availability and next steps.
          </p>
          <p className="mt-8 text-[15px] text-[#6B5A49]">
            Prefer email?{" "}
            <a
              data-testid="inquiry-email-fallback"
              href={`mailto:${EMAIL}`}
              className="text-[#2A1F16] underline decoration-[#B8893B] underline-offset-4 hover:text-[#96754D] transition-colors"
            >
              {EMAIL}
            </a>
          </p>
          <p className="mt-6 text-xs leading-relaxed text-[#9C8871] max-w-sm">
            Submitting an inquiry does not confirm your date — bookings are confirmed personally by
            Mrinalee after availability is checked.
          </p>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="bg-[#FFFCF7] border border-[#E9DCC8] rounded-2xl p-6 sm:p-10 shadow-[0_30px_60px_-40px_rgba(42,31,22,0.35)]">
            <AnimatePresence mode="wait">
              {status === "success" ? (
                <motion.div
                  key="success"
                  data-testid="inquiry-success-message"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="text-center py-16"
                >
                  <CheckCircle2 className="mx-auto text-[#B8893B]" size={44} strokeWidth={1.4} />
                  <h3 className="font-serif-ed text-2xl sm:text-3xl mt-6 text-[#2A1F16]">Thank you for your inquiry.</h3>
                  <p className="mt-3 text-[15px] text-[#6B5A49]">Mrinalee will get back to you shortly.</p>
                  <button
                    data-testid="inquiry-send-another-button"
                    onClick={() => { setForm(EMPTY); setStatus("idle"); }}
                    className="mt-8 text-[11px] uppercase tracking-[0.22em] text-[#96754D] hover:text-[#2A1F16] transition-colors"
                  >
                    Send another inquiry
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  data-testid="booking-inquiry-form"
                  onSubmit={submit}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="grid sm:grid-cols-2 gap-x-6 gap-y-7"
                  noValidate={false}
                >
                  <div>
                    <Label htmlFor="inq-name" required>Name</Label>
                    <input id="inq-name" data-testid="input-full-name" required value={form.name} onChange={set("name")} className={fieldCls} placeholder="Your full name" autoComplete="name" />
                  </div>
                  <div>
                    <Label htmlFor="inq-email" required>Email</Label>
                    <input id="inq-email" data-testid="input-email-address" type="email" required value={form.email} onChange={set("email")} className={fieldCls} placeholder="you@example.com" autoComplete="email" />
                  </div>
                  <div>
                    <Label htmlFor="inq-phone">Phone / WhatsApp</Label>
                    <input id="inq-phone" data-testid="input-phone-number" type="tel" value={form.phone} onChange={set("phone")} className={fieldCls} placeholder="+91 ..." autoComplete="tel" />
                  </div>
                  <div>
                    <Label htmlFor="inq-event" required>Event type</Label>
                    <select id="inq-event" data-testid="select-event-type" required value={form.event_type} onChange={set("event_type")} className={`${fieldCls} ${form.event_type ? "" : "text-[#9C8871]"}`}>
                      <option value="" disabled>Select event type</option>
                      {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="inq-date" required>Event date</Label>
                    <input id="inq-date" data-testid="input-event-date" type="date" required min={today} value={form.event_date} onChange={set("event_date")} className={fieldCls} />
                  </div>
                  <div>
                    <Label htmlFor="inq-location">Event location</Label>
                    <input id="inq-location" data-testid="input-event-location" value={form.location} onChange={set("location")} className={fieldCls} placeholder="City / venue" />
                  </div>
                  <div>
                    <Label htmlFor="inq-people">Number of people</Label>
                    <input id="inq-people" data-testid="input-guest-count" type="number" min="1" max="50" value={form.guest_count} onChange={set("guest_count")} className={fieldCls} placeholder="e.g. 2" />
                  </div>
                  <div>
                    <Label htmlFor="inq-style">Makeup style / requirements</Label>
                    <input id="inq-style" data-testid="select-preferred-style" value={form.style_preference} onChange={set("style_preference")} className={fieldCls} placeholder="e.g. soft glam, natural" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="inq-message">Additional message</Label>
                    <textarea id="inq-message" data-testid="textarea-additional-details" rows={4} value={form.message} onChange={set("message")} className={`${fieldCls} resize-none`} placeholder="Anything else you'd like me to know..." />
                  </div>

                  <input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    value={form.website}
                    onChange={set("website")}
                    className="hidden"
                  />

                  {status === "error" && (
                    <p data-testid="inquiry-error-message" role="alert" className="sm:col-span-2 text-[15px] text-[#B4533F]">
                      {error}
                    </p>
                  )}

                  <div className="sm:col-span-2 pt-2">
                    <button
                      data-testid="inquiry-form-submit-button"
                      type="submit"
                      disabled={status === "sending"}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-3 text-[11px] uppercase tracking-[0.25em] bg-[#C9A25F] text-[#2A1F16] px-10 py-4 rounded-full hover:bg-[#B8893B] disabled:opacity-60 transition-colors duration-400"
                    >
                      {status === "sending" && <Loader2 size={14} className="animate-spin" />}
                      {status === "sending" ? "Sending..." : "Send Inquiry"}
                    </button>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
